import { isExecutor, isGestor, type Me } from './permissions'

/**
 * Leitura do Portal de Chamados (pc.flexibase.com.br) — chamados recebidos
 * por cada área.
 *
 * O PC tem banco próprio (outro Supabase), então a leitura passa pela API de
 * integração dele (`/api/integracoes/apps/supply/*`, só GET), com o token do
 * dev01 da própria pessoa. Quem decide o que volta são as permissões da
 * pessoa no PC; o Supply só escolhe quais departamentos pedir
 * (`supply.area.pc_area`). Regras puras aqui, testadas em `pc.test.ts`.
 */

export const PC_URL = 'https://pc.flexibase.com.br'

export type PcTicketStatus = 'Aberto' | 'Em Andamento' | 'Pausado' | 'Concluído' | 'Cancelado'

/** Rótulo curto do status aberto (o PC chama de "Aberto" o que ainda ninguém pegou). */
export const PC_OPEN_STATUS_LABEL: Record<'Aberto' | 'Em Andamento' | 'Pausado', string> = {
  Aberto: 'Aguardando',
  'Em Andamento': 'Em andamento',
  Pausado: 'Pausado',
}

/** Departamento que recebe chamados no PC. `permitido` = a pessoa vê os chamados dele no PC. */
export interface PcArea {
  readonly nome: string
  readonly permitido: boolean
}

/** Números de um departamento (`GET /chamados/resumo`). Sem permissão, só `area` e `permitido`. */
export interface PcAreaSummary {
  readonly area: string
  readonly permitido: boolean
  readonly abertos?: number
  readonly aguardando?: number
  readonly em_andamento?: number
  readonly pausados?: number
  /** Abertos com o prazo de resolução (SLA) vencido. */
  readonly atrasados?: number
  readonly mais_antigo_em?: string | null
  readonly recebidos_no_periodo?: number
  readonly concluidos_no_periodo?: number
}

export interface PcSummary {
  readonly periodo: { readonly de: string; readonly ate: string }
  readonly gerado_em: string
  readonly areas: readonly PcAreaSummary[]
}

/** Chamado aberto (`GET /chamados`). */
export interface PcTicket {
  readonly id: string
  readonly protocolo: string
  readonly assunto: string
  readonly status: PcTicketStatus
  readonly prioridade: string | null
  readonly area: string
  readonly solicitante: string | null
  readonly responsavel: string | null
  /** Está com quem pediu a lista. */
  readonly comigo: boolean
  /** Instante (ISO) da abertura. */
  readonly criado_em: string
  /** Prazo de resolução (SLA), instante ISO. */
  readonly prazo: string | null
  readonly atrasado: boolean
  readonly url: string
}

export interface PcTicketPage {
  readonly pagina: number
  readonly limite: number
  readonly total: number
  readonly itens: readonly PcTicket[]
  /** Departamentos pedidos que a pessoa não vê no PC. */
  readonly sem_permissao: readonly string[]
}

/** O PC compara departamento sem diferenciar maiúsculas (`area_destino.ilike`). */
export function pcAreaKey(nome: string): string {
  return nome.trim().toUpperCase()
}

/** Link direto para o chamado no PC (a `url` da API, ou montada pelo id). */
export function pcTicketUrl(ticket: Pick<PcTicket, 'id'> & { url?: string }): string {
  return ticket.url && ticket.url.startsWith(`${PC_URL}/`) ? ticket.url : `${PC_URL}/chamados/${encodeURIComponent(ticket.id)}`
}

export interface PcLinkedArea {
  readonly id: string
  readonly code: string
  readonly pc_area: string | null
  readonly is_active: boolean
}

/**
 * Áreas do Supply com vínculo no PC que a pessoa acompanha: gestor, todas;
 * gerente, colaborador e executor, as áreas em que têm vínculo (o papel na
 * área vem de `area_member`, a mesma regra de `supply.is_gerente_of`/
 * `supply.is_executor`). O PC ainda filtra pelas permissões dele.
 */
export function pcScopeAreas<A extends PcLinkedArea>(me: Me | null | undefined, areas: readonly A[]): A[] {
  if (!me) return []
  const linked = areas.filter((a) => a.is_active && !!a.pc_area?.trim())
  if (isGestor(me)) return linked
  const mine = new Set(me.areas.map((a) => a.areaId))
  return linked.filter((a) => mine.has(a.id))
}

/** Departamentos do PC sem repetir (duas áreas do Supply podem apontar para o mesmo). */
export function pcDepartments(areas: readonly Pick<PcLinkedArea, 'pc_area'>[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const a of areas) {
    const nome = a.pc_area?.trim()
    if (!nome || seen.has(pcAreaKey(nome))) continue
    seen.add(pcAreaKey(nome))
    out.push(nome)
  }
  return out
}

/**
 * O que a Minha semana mostra: o gerente (e o gestor) vê os abertos das áreas;
 * o executor vê os que estão com ele e, abaixo, os da área dele (ADR 0006: o
 * executor lê o time, sem visão gerencial — sem contadores por área).
 */
export function pcWeekView(me: Me | null | undefined): 'gerencia' | 'executor' {
  return isExecutor(me) ? 'executor' : 'gerencia'
}

export interface PcTotals {
  readonly abertos: number
  readonly atrasados: number
  readonly recebidos: number
  readonly concluidos: number
  /** Departamentos com números (a pessoa vê no PC). */
  readonly visiveis: number
}

/** Soma dos departamentos que a pessoa vê (o mesmo departamento conta uma vez). */
export function pcTotals(areas: readonly PcAreaSummary[]): PcTotals {
  const seen = new Set<string>()
  let abertos = 0
  let atrasados = 0
  let recebidos = 0
  let concluidos = 0
  for (const a of areas) {
    if (!a.permitido || seen.has(pcAreaKey(a.area))) continue
    seen.add(pcAreaKey(a.area))
    abertos += a.abertos ?? 0
    atrasados += a.atrasados ?? 0
    recebidos += a.recebidos_no_periodo ?? 0
    concluidos += a.concluidos_no_periodo ?? 0
  }
  return { abertos, atrasados, recebidos, concluidos, visiveis: seen.size }
}

/** Dias corridos desde a abertura (para "aberto há N dias"), no fuso de São Paulo. */
export function pcAgeDays(createdAt: string, today: string): number {
  const opened = new Date(createdAt)
  if (Number.isNaN(opened.getTime())) return 0
  const day = new Date(opened.getTime() - 3 * 3600_000).toISOString().slice(0, 10)
  return Math.max(0, Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${day}T00:00:00Z`)) / 86_400_000))
}
